import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { RequestTypes } from './requestTypes'
import { foldersKeys } from '../../folders/foldersKeys'
import { siteBoxDocumentsKeys } from '../../siteBoxDocuments'

const projectPlansQueryKeys = createQueryKeys('project-plan', {
  byProjectLocationPlan: ({
    planId,
    projectId,
    nodeId,
  }: RequestTypes['useGetProjectPlan']) => {
    return {
      queryKey: [projectId, nodeId, planId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `projects/${projectId}/locations/nodes/${nodeId}/plans/${planId}`,
          {},
        )

        return data
      },
    }
  },
  byProject: ({ projectId }: RequestTypes['useGetProjectPlans']) => {
    return {
      queryKey: [projectId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `projects/${projectId}/locations/plans`,
          {},
        )

        return data.plans
      },
    }
  },
})

const projectPlansMutationKeys = createMutationKeys('project-plan', {
  updateProjectPlan: {
    mutationKey: null,
    mutationFn: async ({
      projectId,
      nodeId,
      planId,
      areas,
    }: RequestTypes['updateProjectPlan']) => {
      const { data } = await APIAxiosInstance.put(
        `projects/${projectId}/locations/nodes/${nodeId}/plans/${planId}`,
        {
          areas,
        },
      )

      return data
    },
  },
  uploadProjectPlanSitebox: {
    mutationKey: null,
    mutationFn: async ({
      organisationId,
      projectId,
      nodeId,
      file,
    }: RequestTypes['uploadProjectPlanSitebox']) => {
      if (file?.id) {
        const { data } = await APIAxiosInstance.post(
          `projects/${projectId}/locations/nodes/${nodeId}/plans/upload-sitebox`,
          {
            siteBoxDocumentId: file.id,
          },
        )

        return data
      } else {
        const rootFolder = await foldersKeys
          .byId({
            organisationId,
            projectId,
            type: 'SITE_BOX',
            folderId: -1,
            status: ['LIVE'],
          })
          .queryFn(undefined)

        let orgPlansFolder = rootFolder.folders.find(
          (folder) => folder.displayName === 'Plans',
        )

        if (!orgPlansFolder) {
          orgPlansFolder = await (foldersKeys.createFolder as any).mutationFn({
            organisationId,
            projectId,
            type: 'SITE_BOX',
            folderName: 'Plans',
          })
        }

        const siteBoxDocument = await (
          siteBoxDocumentsKeys.createDocument as any
        ).mutationFn({
          organisationId,
          projectId,
          version: {
            documentName: file.name,
            format: 'PDF',
          },
          folderId: orgPlansFolder?.id,
          file,
          folderName: `organisation-${organisationId}-project-${projectId}-site-box-documents`,
        })

        const { data } = await APIAxiosInstance.post(
          `projects/${projectId}/locations/nodes/${nodeId}/plans/upload-sitebox`,
          {
            siteBoxDocumentId: siteBoxDocument?.id,
          },
        )

        return data
      }
    },
  },
  publishProjectPlan: {
    mutationKey: null,
    mutationFn: async ({
      projectId,
      nodeId,
      planId,
    }: RequestTypes['publishProjectPlan']) => {
      const { data } = await APIAxiosInstance.put(
        `projects/${projectId}/locations/nodes/${nodeId}/plans/${planId}/publish`,
        {},
      )

      return data
    },
  },
  deleteProjectPlan: {
    mutationKey: null,
    mutationFn: async ({
      projectId,
      nodeId,
      planId,
    }: RequestTypes['deleteProjectPlan']) => {
      const { data } = await APIAxiosInstance.delete(
        `projects/${projectId}/locations/nodes/${nodeId}/plans/${planId}`,
      )

      return data
    },
  },
})

const projectPlansKeys = mergeQueryKeys(
  projectPlansQueryKeys,
  projectPlansMutationKeys,
)['project-plan']

export { projectPlansKeys }
